// Réalisations des tournées (toutes tournées) : filtres par résultat, période de trente jours, motifs des
// non-réalisations ; dépôts sauvages (signalements de catégorie depot_sauvage de la partie A, orientés
// vers le service de la propreté, D-044, D-131) avec leur fiche et leur intervention.
import { CODES_ETATS_REALISATION, ETATS_REALISATION } from '@cc/contrats';

const QUALIFICATIONS: Record<string, string> = { a_qualifier: 'à qualifier', recevable: 'recevable', oriente: 'orienté', rejete: 'rejeté', doublon: 'doublon' };
const libelleQualification = (q: string | null) => (q ? (QUALIFICATIONS[q] ?? q) : 'à qualifier');
import { Bandeau, EtatVide, Filtres, Lien, LienBouton, Panneau, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, ClipboardList } from 'lucide-react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useDepotsSauvages, useRealisations } from './api';
import { resumePoints, TON_REALISATION } from './logique';
import type { Realisation } from './types';
import commun from '../../commun/commun.module.css';

export function PageRealisations() {
  const [filtres, setFiltres] = useFiltresPersistes('proprete-realisations', { etat: '' });
  const parametres: Record<string, string> = { limite: '200' };
  if (filtres.etat) parametres.etat = filtres.etat;
  const liste = useRealisations(parametres);
  const depots = useDepotsSauvages();
  const colonnes: ColonneTableau<Realisation>[] = [
    { id: 'date', entete: 'Date', enTeteLigne: true, masquable: false, cellule: (r) => date(r.date), valeurTri: (r) => r.date },
    { id: 'tournee', entete: 'Tournée', cellule: (r) => <Lien href={`/proprete/tournees/${r.tournee.id}`}>{r.tournee.code ?? ''} {r.tournee.nom ?? ''}</Lien>, valeurTri: (r) => r.tournee.code ?? '' },
    { id: 'etat', entete: 'Résultat', cellule: (r) => <PuceEtat ton={TON_REALISATION[r.etat]} libelle={r.etat_libelle} />, valeurTri: (r) => r.etat },
    { id: 'points', entete: 'Points', cellule: (r) => resumePoints(r) },
    { id: 'motif', entete: 'Motif', cellule: (r) => r.motif ?? '' },
    { id: 'origine', entete: 'Origine', cellule: (r) => (r.origine === 'synchronisation' ? 'Appareil de terrain' : 'Bureau') },
    { id: 'recu', entete: 'Reçue le', cellule: (r) => dateHeure(r.recu_le), valeurTri: (r) => r.recu_le },
  ];
  return (
    <>
      <EnTetePage etiquette="Déchets et propreté" titre="Réalisations des tournées" description="Réalisées, partielles et non réalisées avec leur motif, saisies au bureau ou reçues des appareils de terrain." actions={<LienBouton href="/proprete" icone={ArrowLeft} variante="secondaire">Tournées</LienBouton>} />
      <Filtres groupes={[{ id: 'etat', libelle: 'Résultat', options: [{ valeur: '', libelle: 'Tous' }, ...CODES_ETATS_REALISATION.map((e) => ({ valeur: e, libelle: ETATS_REALISATION[e] }))] }]} valeurs={filtres} onChanger={setFiltres} />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L3-B" fonction="La liste des réalisations">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune réalisation" icone={ClipboardList}><p>Aucune réalisation ne correspond à ce filtre.</p></EtatVide>
        ) : (
          <Tableau legende="Réalisations" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(r) => r.id} cleColonnes="proprete-realisations" />
        )}
      </EtatRequete>
      <Panneau titre="Dépôts sauvages" niveau={2}>
        {depots.data?.raccordement === 'en_attente' ? <Bandeau type="info">{depots.data.message ?? 'Les signalements ne sont pas encore raccordés.'} Les dépôts sauvages sont des signalements de catégorie « dépôt sauvage » orientés vers le service de la propreté (D-044).</Bandeau> : null}
        {depots.data?.raccordement === 'actif' && depots.data.elements.length === 0 ? <p className={commun.na}>Aucun dépôt sauvage signalé.</p> : null}
        {depots.data?.raccordement === 'actif' && depots.data.elements.length > 0 ? (
          <ul aria-label="Dépôts sauvages signalés">
            {depots.data.elements.map((d) => (
              <li key={d.id}>
                <Lien href={`/interventions/signalements/${d.id}`}>
                  <span className={commun.reference}>{d.numero ?? 'sans numéro'}</span>
                </Lien>{' '}
                {d.description ?? ''} {d.adresse ? `(${d.adresse})` : ''} : {libelleQualification(d.qualification)}, signalé le {dateHeure(d.cree_le)}
                {d.service?.nom ? `, orienté vers ${d.service.nom}` : ', sans service désigné'}
                {d.intervention ? (
                  <>
                    {', intervention '}
                    <Lien href={`/interventions/${d.intervention.id}`}>{d.intervention.numero}</Lien>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </Panneau>
    </>
  );
}
